import dayjs from "dayjs";
import type { PdpUrlScore } from "../../../../components/PdpScoreStatus/PdpScoreStatus";
import { brandIsNetwork } from "../../../../utils/plans";
import { movement } from "../../videos/selectedVideoCardV2/performance/pageScore";
import type { ISingleProduct } from "../../productsV2/productListView/constants";

/**
 * The product details page — page-level rules: which message the header call's
 * failure shows, what leaving the product drops from the URL, the `?video=`
 * view and why it changed (the page's scroll rule), the rail's Product pages
 * count, a silent refetch's stable `brands`, how the product payload maps onto
 * the header, and the Product pages movement sentence.
 *
 * Pure on purpose. `src/utils` pulls in @react-pdf at import time and cannot
 * load in the test runner, so role checks arrive as booleans (`internal`),
 * the way pdpAssignment.ts takes `isInternal(role)`.
 */

// ---------------------------------------------------------------------------
// Page states
// ---------------------------------------------------------------------------

/** "none" = leave the current view as it is. */
export type PageErrorKind = "notfound" | "forbidden" | "error" | "none";

/**
 * The request failed because the session is ending: the page is on its way to
 * the login page, so nothing should change on screen. Two shapes:
 *   - a 401 — the response interceptor starts the redirect but still rejects
 *     (api/index.ts:131-137);
 *   - a cancel — an expired token never leaves the browser: the request
 *     interceptor starts the redirect and rejects with `axios.Cancel`
 *     (api/index.ts:78-81). `__CANCEL__` is axios.isCancel's own test
 *     (axios/lib/cancel/isCancel.js), read here without importing axios.
 */
export const isSessionRedirect = (err: any): boolean =>
  // eslint-disable-next-line no-underscore-dangle -- axios's own flag, named by axios
  err?.__CANCEL__ === true || err?.response?.status === 401;

/**
 * The header call's failure → the page message. A copy of the video page's
 * rule (selectedVideoCardV2/index.tsx:104-109) with ONE difference: a session
 * redirect (a 401, or the expired-token cancel) changes nothing. This catch
 * runs mid-redirect; the video page maps a 401 to "forbidden" and a cancel to
 * "error", which would flash "This product isn't in your workspace" or "This
 * page didn't load" before the login page.
 */
export const classifyError = (err: any): PageErrorKind => {
  if (isSessionRedirect(err)) return "none";
  const code = err?.response?.status;
  if (code === 404) return "notfound";
  if (code === 403) return "forbidden";
  return "error";
};

/** The kinds that get a full-page message. */
export type PageMessageKind = Exclude<PageErrorKind, "none">;

/**
 * The `?video=` id the Videos block is showing, or null. The same parse as
 * VideoSection (components/videoSection/VideoSection.tsx:156-159), so the page
 * hides the rail and the other sections exactly when the block swaps to the
 * video (Q1 b) — a junk value like `?video=abc` opens nothing there, and
 * hides nothing here.
 */
export const openVideoId = (raw: string | null | undefined): number | null =>
  raw ? parseInt(raw, 10) || null : null;

// ---------------------------------------------------------------------------
// Leaving the product (brand / network mount)
// ---------------------------------------------------------------------------

/**
 * Every URL param the Videos list keeps its state in. The list keeps every
 * param when it opens a product (productListView/index.tsx:147-149), so any
 * left behind would open the next product filtered, sorted or pages deep.
 *   - the inline video view: video, selectedTab (VideoSection.tsx:231-236, :265-270);
 *   - paging, order and layout: page, limit, sort, view (useVideosQuery.ts:20,
 *     useCommonParams.tsx:60-71, :161-163);
 *   - the Non-project toggle: nonProject (VideoSection.tsx:191-199);
 *   - its filters: VIDEO_V2_FILTER_KEYS (utils/index.js:228-244) without
 *     productId, which leaving drops anyway. Copied, not imported: `src/utils`
 *     can't load in the test runner (see the top of this file).
 * Not `search`: the product list owns it too (listParamsOnLeave).
 */
const VIDEOS_LIST_PARAMS = [
  "video",
  "selectedTab",
  "page",
  "limit",
  "sort",
  "view",
  "nonProject",
  "proId",
  "brandId",
  "aspectRatio",
  "extraFilter",
  "contentCode",
  "productCategoryId",
  "demo",
  "embed",
  "hasPdp",
  "hasJsonLd",
] as const;

/**
 * The list URL the brand / network mount goes back to: this product's URL
 * without productId and the Videos list's params. The product list's own
 * params stay (search, rmn_category_id, rmn_brand_id, product_category_id,
 * welcome: Products.tsx:36-47,70, productListView/index.tsx:118-123).
 *
 * `search` is the one param both lists read (the Videos list shows it as a
 * "Search" chip it can clear, VideoSection.tsx:213,366-371). It goes back to
 * what the product list had when the product opened (`listSearch`), so
 * clearing that chip here never clears the list's search.
 */
export const listParamsOnLeave = (
  current: URLSearchParams,
  listSearch: string | null,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  ["productId", ...VIDEOS_LIST_PARAMS].forEach((key) => next.delete(key));
  if (listSearch) next.set("search", listSearch);
  else next.delete("search");
  return next;
};

// ---------------------------------------------------------------------------
// The open video and the scroll
// ---------------------------------------------------------------------------

/** What the Videos block shows, as the page's scroll rule reads it. */
export interface VideoView {
  /** A `?video=` is showing (the rail and the other sections are hidden). */
  videoOpen: boolean;
  /** The Stories tab is picked (brand users). */
  stories: boolean;
}

/**
 * Why `videoOpen` changed, if it did:
 *   - "open" / "close": the `?video=` param came or went (a card, the nested
 *     Back, the header's "Back to product", the browser's Back / Forward);
 *   - "tab": Videos | Stories flipped while `?video=` stayed set. Picking
 *     Stories hides the video without closing it, and picking Videos shows it
 *     again: neither is an open or a close.
 * Null on the first render (a link that mounts with `?video=`) or when
 * nothing that hides the sections changed.
 */
export const videoViewChange = (prev: VideoView | null, next: VideoView): "open" | "close" | "tab" | null => {
  if (!prev || prev.videoOpen === next.videoOpen) return null;
  if (prev.stories !== next.stories) return "tab";
  return next.videoOpen ? "open" : "close";
};

/**
 * The rail's "Product pages" number (plan "Rail"): the page count from
 * ProductPagesSection's `onCount`, shown at 2 or more. Nothing while the
 * count is unknown (loading, couldn't load) or 0–1. Consumer Voice never
 * carries a number, and Videos has none (D4).
 */
export const pagesChipCount = (count: number | null | undefined): number | undefined =>
  count != null && count >= 2 ? count : undefined;

/**
 * A silent refetch hands back a new `brands` array with the same content.
 * Reuse the previous array when nothing in it changed, so hooks keyed on it
 * (`usePdpUrlBrand`, MODAL/ProductPdpUrlsPanel.tsx:139-141) don't reset a
 * half-filled owner pick. Anything else in the product takes the new values.
 */
export const keepStableBrands = <T extends { brands?: unknown }>(prev: T | null | undefined, next: T): T => {
  if (!prev || prev.brands === next.brands) return next;
  return JSON.stringify(prev.brands) === JSON.stringify(next.brands) ? { ...next, brands: prev.brands } : next;
};

// ---------------------------------------------------------------------------
// Header mappers — GET /brands/:brandId/product/:productId (brands.js:2243-2283)
// ---------------------------------------------------------------------------

export interface HeaderBrand {
  id: number;
  name: string;
  /** Gets the NETWORK chip after its name. */
  network: boolean;
}

/**
 * The brand line. `brands[]` carries `{id, name, rmn}` (brands.js:2255);
 * `brandIsNetwork` reads `rmn` (or a network plan, when a payload carries
 * one) — the same test the rest of the portal uses.
 *
 * Networks go last, as in every handoff shot (01, 04, 08a, 09a; the fixture's
 * brands are in display order, data-model.ts:60). The include has no ORDER BY,
 * so the payload's order is only kept within each group. Display only: the
 * Consumer Voice rule reads `product.brands` itself.
 */
export const headerBrands = (brands?: Array<{ id: number; name?: string | null; rmn?: boolean }> | null): HeaderBrand[] => {
  const line = (Array.isArray(brands) ? brands : [])
    .filter((b) => b?.id && typeof b.name === "string" && b.name.trim())
    .map((b) => ({ id: b.id, name: (b.name as string).trim(), network: brandIsNetwork(b) }));
  return [...line.filter((b) => !b.network), ...line.filter((b) => b.network)];
};

/** The meta card's Category: every category name, " · " between. Null hides the line. */
export const categoriesLabel = (categories?: Array<{ name?: string | null }> | null): string | null => {
  const names = (categories || []).map((c) => (c?.name || "").trim()).filter(Boolean);
  return names.length ? names.join(" · ") : null;
};

/** The meta card's Created: "Mar 4, 2025". Null when the date is missing or unreadable. */
export const createdLabel = (at?: string | null): string | null => {
  if (!at) return null;
  const d = dayjs(at);
  return d.isValid() ? d.format("MMM D, YYYY") : null;
};

/** The 2-line description under the title — `short_description`, hidden when empty. */
export const descriptionText = (raw?: string | null): string | null => {
  const text = typeof raw === "string" ? raw.trim() : "";
  return text || null;
};

export interface ProductHeaderView {
  title: string;
  brands: HeaderBrand[];
  description: string | null;
  imageUrl: string | null;
  category: string | null;
  url: string | null;
  created: string | null;
  /** Staff only (`isInternal`, utils/index.js:92); null hides the row. */
  productId: number | null;
}

export const productHeader = (product: ISingleProduct, internal: boolean): ProductHeaderView => ({
  title: product.title || "",
  brands: headerBrands(product.brands),
  description: descriptionText(product.short_description),
  imageUrl: product.image || null,
  category: categoriesLabel(product.product_categories),
  url: product.url || null,
  created: createdLabel(product.dateCreated),
  productId: internal ? product.desireid : null,
});

/**
 * The meta card's Product URL link target. `url` is free text a user typed
 * or an import scraped, so only http(s) becomes a link — a `javascript:` or
 * other scheme never reaches an href. A bare "brand.com/p" gets https://, and
 * a protocol-relative "//shop.example.com/p" gets https:.
 *
 * The host is checked as typed, not left to `new URL`: Chrome turns
 * "/product/1/x" into https://product/1/x and "Some Product Name" into
 * https://some%20product%20name/ (jsdom throws on both, so jest alone can't
 * see it). Stored values include both (relative paths, plain product names).
 * A host needs a dot and no whitespace or %; else null.
 * Null = print the text without a link.
 */
export const productUrlHref = (raw?: string | null): string | null => {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) return null;
  const absolute = text.startsWith("//") ? `https:${text}` : text;
  // A site-relative path: there is no host to link to.
  if (absolute.startsWith("/")) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(absolute) ? absolute : `https://${absolute}`;
  // The authority as typed, up to the first / ? # (userinfo and port dropped).
  const authority = /^https?:\/\/([^/?#]*)/i.exec(withScheme)?.[1] ?? "";
  const host = authority.slice(authority.lastIndexOf("@") + 1).replace(/:\d*$/, "");
  if (!host.includes(".") || /[\s%]/.test(host)) return null;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.hostname.includes(".") && !u.hostname.includes("%") ? u.href : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Movement sentence — Product pages
// ---------------------------------------------------------------------------

export interface MovementSentence {
  /** Signed; 0 = the neutral "0" chip. */
  change: number;
  /** "up from 70 on 10 Sep" · "down from 66 on 3 Sep" · "same as 70 on 10 Sep". */
  text: string;
}

/**
 * The words after the delta chip. Kind, `change` and `from` come from the
 * live `movement()` (pageScore.ts:305-313); only the date words are the
 * caller's (productPages/pageModel `pageMovement` dates the previous score
 * "on 10 Sep"). `history` is newest first. Null for a first score, or when
 * the series is missing — the caller then says nothing about movement.
 */
export const movementSentence = (
  history: PdpUrlScore[] | null | undefined,
  when: (now: PdpUrlScore, prev: PdpUrlScore) => string,
): MovementSentence | null => {
  const m = movement(history);
  if (m.kind === "first" || !history) return null;
  const [now, prev] = history;
  const words = when(now, prev);
  const tail = words ? ` ${words}` : "";
  if (m.kind === "zero") return { change: 0, text: `same as ${prev.overall_score}${tail}` };
  return { change: m.change, text: `${m.change > 0 ? "up" : "down"} from ${m.from}${tail}` };
};
